import React, { useState, useEffect } from 'react';
import './ShiftTypeManagement.css';
import authService from '../services/auth.service';
import apiService from '../services/api.service';

// Time conversion utilities
const minutesToTimeString = (minutes) => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
};

const timeStringToMinutes = (timeString) => {
  const [hours, minutes] = timeString.split(':').map(Number);
  return hours * 60 + minutes;
};

const ShiftTypeManagement = () => {
  const [shiftTypes, setShiftTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Form state
  const [showForm, setShowForm] = useState(false);
  const [formMode, setFormMode] = useState('add'); // 'add' or 'edit'
  const [currentShiftType, setCurrentShiftType] = useState({
    name: '',
    defaultStartTime: '',
    defaultEndTime: '',
    color: '#3498db',
    isDefault: false,
    notes: ''
  });
  
  // Check if user is admin
  const isAdmin = authService.getUser()?.role === 'admin';
  
  // Fetch shift types
  const fetchShiftTypes = async () => {
    setLoading(true);
    try {
      const response = await apiService.getShiftTypes();
      setShiftTypes(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching shift types:', err);
      setError('Vardiya türleri yüklenirken bir hata oluştu: ' + err.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Load data on component mount
  useEffect(() => {
    fetchShiftTypes();
  }, []);
  
  // Reset form
  const resetForm = () => {
    setCurrentShiftType({
      name: '',
      defaultStartTime: '09:00',
      defaultEndTime: '17:00',
      color: '#3498db',
      isDefault: false,
      notes: ''
    });
  };
  
  // Handle add new
  const handleAddNew = () => {
    resetForm();
    setFormMode('add');
    setShowForm(true);
  };
  
  // Handle edit
  const handleEdit = (shiftType) => {
    // Convert minutes to time strings
    const startTime = minutesToTimeString(shiftType.defaultStartTime);
    const endTime = minutesToTimeString(shiftType.defaultEndTime);
    
    setCurrentShiftType({
      id: shiftType.id,
      name: shiftType.name,
      defaultStartTime: startTime,
      defaultEndTime: endTime,
      color: shiftType.color,
      isDefault: shiftType.isDefault,
      notes: shiftType.notes || ''
    });
    
    setFormMode('edit');
    setShowForm(true);
  };
  
  // Handle input change
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCurrentShiftType({
      ...currentShiftType,
      [name]: type === 'checkbox' ? checked : value
    });
  };
  
  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Convert time strings to minutes
      const startTimeMinutes = timeStringToMinutes(currentShiftType.defaultStartTime);
      const endTimeMinutes = timeStringToMinutes(currentShiftType.defaultEndTime);
      
      if (startTimeMinutes >= endTimeMinutes) {
        setError('Başlangıç saati bitiş saatinden önce olmalıdır!');
        return;
      }
      
      const shiftTypeData = {
        ...currentShiftType,
        defaultStartTime: startTimeMinutes,
        defaultEndTime: endTimeMinutes
      };
      
      if (formMode === 'add') {
        await apiService.createShiftType(shiftTypeData);
      } else {
        // Edit mode
        await apiService.updateShiftType(currentShiftType.id, shiftTypeData);
      }
      
      // Close form and refresh data
      setShowForm(false);
      fetchShiftTypes();
    } catch (err) {
      console.error('Error saving shift type:', err);
      setError('Vardiya türü kaydedilirken bir hata oluştu: ' + 
        (err.response?.data?.error || err.message));
    }
  };
  
  // Handle delete
  const handleDelete = async (id) => {
    if (!window.confirm('Bu vardiya türünü silmek istediğinizden emin misiniz?')) {
      return;
    }
    
    try {
      await apiService.deleteShiftType(id);
      
      // Refresh data
      fetchShiftTypes();
    } catch (err) {
      console.error('Error deleting shift type:', err);
      setError('Vardiya türü silinirken bir hata oluştu: ' + 
        (err.response?.data?.error || err.message));
    }
  };
  
  return (
    <div className="shift-type-management">
      <div className="section-header">
        <h2>Vardiya Türleri</h2>
        {isAdmin && (
          <button 
            className="add-button"
            onClick={handleAddNew}
          >
            Yeni Vardiya Türü Ekle
          </button>
        )}
      </div>
      
      {loading ? (
        <div className="loading">Yükleniyor...</div>
      ) : error ? (
        <div className="error-message">{error}</div>
      ) : (
        <div className="shift-types-container">
          {shiftTypes.length === 0 ? (
            <div className="no-data">Kayıtlı vardiya türü bulunamadı</div>
          ) : (
            <table className="shift-types-table">
              <thead>
                <tr>
                  <th style={{ width: '40px' }}>Renk</th>
                  <th>İsim</th>
                  <th>Saatler</th>
                  <th>Varsayılan</th>
                  <th>Notlar</th>
                  <th style={{ width: '120px' }}>İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {shiftTypes.map(type => (
                  <tr key={type.id}>
                    <td>
                      <div 
                        className="color-box" 
                        style={{ backgroundColor: type.color }}
                      ></div>
                    </td>
                    <td>{type.name}</td>
                    <td>
                      {minutesToTimeString(type.defaultStartTime)} - {minutesToTimeString(type.defaultEndTime)}
                    </td>
                    <td>
                      {type.isDefault ? (
                        <span className="status-badge active">Evet</span>
                      ) : (
                        <span className="status-badge inactive">Hayır</span>
                      )}
                    </td>
                    <td>{type.notes || '-'}</td>
                    <td className="actions-cell">
                      <button 
                        className="edit-button" 
                        onClick={() => handleEdit(type)}
                        title="Vardiya Türünü Düzenle"
                      >
                        <span className="icon">✏️</span>
                        <span className="button-text">Düzenle</span>
                      </button>
                      
                      {!type.isDefault && isAdmin && (
                        <button 
                          className="delete-button" 
                          onClick={() => handleDelete(type.id)}
                          title="Vardiya Türünü Sil"
                        >
                          <span className="icon">🗑</span>
                          <span className="button-text">Sil</span>
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
      
      {/* Add/Edit Form Modal */}
      {showForm && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>{formMode === 'add' ? 'Yeni Vardiya Türü Ekle' : 'Vardiya Türü Düzenle'}</h3>
              <button 
                className="close-button"
                onClick={() => setShowForm(false)}
              >
                &times;
              </button>
            </div>
            
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name">İsim *</label>
                  <input 
                    type="text" 
                    id="name" 
                    name="name" 
                    value={currentShiftType.name} 
                    onChange={handleInputChange} 
                    required 
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="color">Renk</label>
                  <div className="color-picker-wrapper">
                    <input 
                      type="color" 
                      id="color" 
                      name="color" 
                      value={currentShiftType.color} 
                      onChange={handleInputChange} 
                    />
                    <span className="color-value">{currentShiftType.color}</span>
                  </div>
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="defaultStartTime">Varsayılan Başlangıç Saati *</label>
                  <input 
                    type="time" 
                    id="defaultStartTime" 
                    name="defaultStartTime" 
                    value={currentShiftType.defaultStartTime} 
                    onChange={handleInputChange} 
                    required 
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="defaultEndTime">Varsayılan Bitiş Saati *</label>
                  <input 
                    type="time" 
                    id="defaultEndTime" 
                    name="defaultEndTime" 
                    value={currentShiftType.defaultEndTime} 
                    onChange={handleInputChange} 
                    required 
                  />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group checkbox-group">
                  <label>
                    <input 
                      type="checkbox" 
                      name="isDefault" 
                      checked={currentShiftType.isDefault} 
                      onChange={handleInputChange} 
                      disabled={formMode === 'edit' && currentShiftType.isDefault}
                    />
                    Varsayılan Vardiya Türü
                  </label>
                  {formMode === 'edit' && currentShiftType.isDefault && (
                    <span className="helper-text">Varsayılan vardiya türleri düzenlenebilir ama silinemez.</span>
                  )}
                </div>
              </div>
              
              <div className="form-group">
                <label htmlFor="notes">Notlar</label>
                <textarea 
                  id="notes" 
                  name="notes" 
                  value={currentShiftType.notes || ''} 
                  onChange={handleInputChange} 
                  rows="3"
                ></textarea>
              </div>
              
              <div className="form-actions">
                <button 
                  type="button" 
                  className="cancel-button"
                  onClick={() => setShowForm(false)}
                >
                  İptal
                </button>
                <button 
                  type="submit" 
                  className="save-button"
                >
                  Kaydet
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShiftTypeManagement;